import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Checkbox, Container } from 'semantic-ui-react';
import { Media } from '../../../../mediaSetting';
import Modal from 'components/shared/modal/content';
import PopupHeader from 'components/pages/popupHeader';
import Info from 'components/pages/info.js';
import Txt from 'components/inputs/textInput.js';
import SearchSelectUsers from 'components/inputs/searchSelectUsers';
import { showHideOrganisationalUnitAddUsersModal } from 'actions/modal.js';
import { clearSearchUserForm } from 'actions/user.js';

import {
  selectRoleAddUsersForm,
  unSelectRoleAddUsersForm,
  updateMessageAddUsersForm,
  addUsersToOrganisationalUnit,
  clearAddUsersForm,
} from 'actions/organisation';

export default function OrganisationalUnitAddUsersModal(props) {
  const dispatch = useDispatch();
  const selectedUsers = useSelector((state) => state.usersReducer.searchSelectUsersForm.selectedUsers);
  const addUsersForm = useSelector((state) => state.organisationsReducer.addUsersForm);
  const messageData = useSelector((state) => state.globalMessageReducer.messageData);
  const [isAddingUsers, setIsAddingUsers] = useState(false);

  const onSaveButtonClick = (e) => {
    // exit now if event type is not a click (https://jira.attendanywhere.com/browse/VCM-5061)
    if (!e.isTrusted) return;

    // exit now if form is invalid
    if (selectedUsers.length === 0 || addUsersForm.selectedRoles.length === 0 || isAddingUsers) return;

    const requestModel = {
      userInvites: selectedUsers.filter((user) => !user.isEmailOnlyUser).map((user) => user.id),
      emailInvites: selectedUsers.filter((user) => user.isEmailOnlyUser).map((user) => user.email),
      roles: addUsersForm.selectedRoles,
      invitationMessage: addUsersForm.message,
    };

    setIsAddingUsers(true);
    let cb = () => {
      setIsAddingUsers(false);
    };

    dispatch(addUsersToOrganisationalUnit(props.organisationalUnit, requestModel, cb));
  };

  const onCancelButtonClick = (event) => {
    if (!event.target.className.includes('disable')) {
      dispatch(showHideOrganisationalUnitAddUsersModal(false));
      dispatch(clearSearchUserForm());
      dispatch(clearAddUsersForm());
    }
  };

  const onInviteMessageChange = (value) => {
    dispatch(updateMessageAddUsersForm(value));
  };

  const onRoleNameCheckboxChange = (isChecked, roleId) => {
    isChecked ? dispatch(selectRoleAddUsersForm(roleId)) : dispatch(unSelectRoleAddUsersForm(roleId));
  };

  const title = APP.LANGUAGE.OrganisationalUnitAddUsersModal.Title;
  const subtitle = `${props.organisationalUnit.name} (${props.organisationalUnit.abbreviatedName})`;

  // Right panel
  let buttonClasses = 'ui labeled icon button left-text color-white max-width  mrlnull mrbnull';

  let cancelButtonClasses = buttonClasses + ' color-bad-things color-bad-things-hover mrtm';
  let addUserButtonClasses = buttonClasses + ' color-button color-button-hover';
  let cancelButtonIconClasses = 'remove icon';
  // disable Add button of form state not valid
  if (
    selectedUsers.length === 0 ||
    addUsersForm.selectedRoles.length === 0 ||
    (messageData && messageData.text.toString().indexOf(APP.LANGUAGE.ServerErrors.InvalidEmailGeneralError) > -1)
  )
    addUserButtonClasses += ' disable';

  // show loading indicator
  if (isAddingUsers) {
    addUserButtonClasses += ' loading';
    cancelButtonClasses += ' disable';
    cancelButtonIconClasses += ' disable';
  }

  const rightPanel = (
    <div>
      <div className="ui actions mrtxxs mrbl max-width">
        <button tabIndex="4" type="button" onClick={onSaveButtonClick} className={addUserButtonClasses}>
          {APP.LANGUAGE.Save}
          <i className="checkmark icon" />
        </button>
        <button tabIndex="5" type="button" onClick={onCancelButtonClick} className={cancelButtonClasses}>
          <i className={cancelButtonIconClasses} onClick={onCancelButtonClick} />
          {APP.LANGUAGE.Cancel}
        </button>
      </div>
      <Info
        paragraphs={[
          APP.LANGUAGE.OrganisationalUnitAddUsersModal.InfoPartOne,
          APP.LANGUAGE.OrganisationalUnitAddUsersModal.InfoPartTwo,
        ]}
      />
    </div>
  );

  const availableRolesMarkUp = (isOnDesktop) => {
    return props.availableRoles.map((role, index) => {
      return (
        <Checkbox
          name={role.name}
          key={role.id}
          checked={!!addUsersForm.selectedRoles.find((id) => id === role.id)}
          tabIndex={index}
          className={'div-like ' + (isOnDesktop ? 'mrbs' : 'mrbm')}
          onClick={(e, data) => {
            onRoleNameCheckboxChange(data.checked, role.id);
          }}
          label={role.name}
        />
      );
    });
  };

  return (
    <Modal show={true} hideWhenClickAway={false}>
      <PopupHeader helpLink={APP.EXTERNAL_LINKS.ManageUsers.HelpAddUsers} title={title} subtitle={subtitle} />
      <div className="content">
        <div className="main-ui-container manage-roles-for-users-modal">
          <div id="righthandfloatingpanel">
            <div className="right ui rail">
              <div className="ui sticky">{rightPanel}</div>
            </div>
            <div>
              <div className="ui form grid">
                <div className="sixteen wide column">
                  <SearchSelectUsers
                    allowNewInvite={true}
                    allowMultipleUserSelection={true}
                    dispatch={dispatch}
                    entityType={'Organisational Unit'}
                  />
                </div>
                <div className="sixteen wide column pdtnull pdbxxs">
                  <div className="placeholder-label color-placeholder div-like mrbs">
                    {APP.LANGUAGE.OrganisationalUnitAddUsersModal.SelectRolesFieldLabel}
                  </div>
                </div>
                <div className="sixteen wide column pdtnull pdbnull">
                  <Container fluid as={Media} lessThan="computer">
                    {availableRolesMarkUp(false)}
                  </Container>
                  <Container fluid as={Media} greaterThanOrEqual="computer">
                    {availableRolesMarkUp(true)}
                  </Container>
                </div>
                <div className="sixteen wide column mrtl mrbm">
                  <Txt
                    textArea
                    tabIndex="2"
                    placeholder={APP.LANGUAGE.OrganisationalUnitAddUsersModal.InvitationMessageFieldLabel}
                    notRenderPlaceholderAsTitle={true}
                    value={addUsersForm.message}
                    onChange={(value) => {
                      onInviteMessageChange(value);
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="bottom-rail-with-left-and-right-content mrtl">{rightPanel}</div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

OrganisationalUnitAddUsersModal.propTypes = {
  organisationalUnit: PropTypes.object.isRequired,
  availableRoles: PropTypes.array.isRequired,
};
